/**
 * Capacitor native plugin wrappers.
 * These safely check if running in a native context before calling native APIs.
 * Falls back gracefully on web.
 */
import { Capacitor } from '@capacitor/core';

export const isNative = () => Capacitor.isNativePlatform();
export const getPlatform = () => Capacitor.getPlatform(); // 'web' | 'ios' | 'android'

// ── Push Notifications ──────────────────────────────────────────────
export async function initPushNotifications(onTokenReceived, onNotificationReceived) {
  if (!isNative()) return null;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    const permResult = await PushNotifications.requestPermissions();
    if (permResult.receive !== 'granted') return null;

    await PushNotifications.register();

    PushNotifications.addListener('registration', (token) => {
      console.log('[Push] Token:', token.value);
      onTokenReceived?.(token.value);
    });

    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('[Push] Received:', notification);
      onNotificationReceived?.(notification);
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.log('[Push] Action:', action);
    });

    return true;
  } catch (e) {
    console.warn('[Push] Not available:', e);
    return null;
  }
}

// ── Geolocation ─────────────────────────────────────────────────────
export async function getCurrentPosition() {
  if (isNative()) {
    try {
      const { Geolocation } = await import('@capacitor/geolocation');
      const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true });
      return { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
    } catch (e) {
      console.warn('[Geo] Native failed, falling back to web:', e);
    }
  }
  // Web fallback
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error('Geolocation not supported'));
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      (err) => reject(err),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  });
}

// ── Haptics ─────────────────────────────────────────────────────────
export async function hapticFeedback(style = 'medium') {
  if (!isNative()) return;
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    const map = { light: ImpactStyle.Light, medium: ImpactStyle.Medium, heavy: ImpactStyle.Heavy };
    await Haptics.impact({ style: map[style] || ImpactStyle.Medium });
  } catch (e) {
    // Silently fail on web
  }
}

export async function hapticNotification(type = 'success') {
  if (!isNative()) return;
  try {
    const { Haptics, NotificationType } = await import('@capacitor/haptics');
    const map = { success: NotificationType.Success, warning: NotificationType.Warning, error: NotificationType.Error };
    await Haptics.notification({ type: map[type] || NotificationType.Success });
  } catch (e) {
    // Silently fail
  }
}

// ── Status Bar ──────────────────────────────────────────────────────
export async function setStatusBarStyle(isDark = true) {
  if (!isNative()) return;
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light });
  } catch (e) {
    // Silently fail
  }
}

// ── Splash Screen ───────────────────────────────────────────────────
export async function hideSplashScreen() {
  if (!isNative()) return;
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide();
  } catch (e) {
    // Silently fail
  }
}

// ── App (back button, state) ────────────────────────────────────────
export async function initAppListeners(onBackButton, onStateChange) {
  if (!isNative()) return;
  try {
    const { App } = await import('@capacitor/app');

    App.addListener('backButton', ({ canGoBack }) => {
      if (onBackButton) {
        onBackButton(canGoBack);
      } else if (canGoBack) {
        window.history.back();
      } else {
        App.exitApp();
      }
    });

    App.addListener('appStateChange', ({ isActive }) => {
      onStateChange?.(isActive);
    });
  } catch (e) {
    // Silently fail
  }
}

// ── Keyboard ────────────────────────────────────────────────────────
export async function initKeyboardListeners(onShow, onHide) {
  if (!isNative()) return;
  try {
    const { Keyboard } = await import('@capacitor/keyboard');
    Keyboard.addListener('keyboardWillShow', (info) => onShow?.(info.keyboardHeight));
    Keyboard.addListener('keyboardWillHide', () => onHide?.());
  } catch (e) {
    // Silently fail
  }
}
